import Button from 'react-bootstrap/Button'
import React from "react";
import { ClaimModel } from "../../../../models/ClaimModel";
import { MarketingVisitEntry } from "../../../../models/MarketingVisitEntry";
import { DateField, MoneyField, MultiLineField, NumberField, NumericSelectField, TextField, TextMultiSelectField, validateFields } from "../../../../shared/fields";
import { Option } from "../../../../shared/fields/Options";
import { PercentageField } from "../../../../shared/fields/PercentageField";
import { ShowIf } from "../../../../shared/fields/ShowIf";
import { ISection, TableColumn } from "../../../../shared/table";
import { calculateDaysInclusive, formatDateForDisplay, formatNumber, getDateFromString, grantPeriodEnd, grantPeriodStart, newGuid, paymentMethodDataSource, customRound, getMarketString, formatPaymentMethodForDisplay, formatMoneyForDisplay, formatPercentageForDisplay, sortSchedules } from "../../../../shared/Utility";

export const marketingVisitEntryColumns = (markets: Option<string>[]): TableColumn<MarketingVisitEntry>[] => [
    { name: "Trip No.", content: d => d.tripNo },
    { name: "Purpose of trip", content: d => d.purpose, printOnly: true },
    { name: "Destination(s)", content: d => { return getMarketString(d.markets, markets); }, responsive: "md" },
    { name: "Name and position of traveller", content: d => { return <span>{d.name}<br />{d.position}</span> } },
    {
        name: "Travel departure - return date",
        content: d => { return <span>{formatDateForDisplay(d.departureDate)} <span>-</span><br /> {formatDateForDisplay(d.returnDate) }</span> }, printOnly: true },
    {
        name: "Describe the expense",
        content: d => {
            if (d.expenseType == VisitExpenseType.Airfare)
                return 'Airfare'
            else if (d.expenseType == VisitExpenseType.AirTicketCostsOther)
                return 'Air ticket costs - other'
            else if (d.expenseType == VisitExpenseType.OVAOnly)
                return 'OVA Only'
            else
                return ''
        },
        printOnly: true
    },
    { name: "Supporting evidence", content: d => d.evidence, printOnly: true },
    { name: "Payment date", content: d => formatDateForDisplay(d.paymentDate) },
    { name: "Payment method", content: d => formatPaymentMethodForDisplay(d.paymentMethod), printOnly: true },
    { name: "Total expenses $A", content: d => formatMoneyForDisplay(d.expenseAmount), printOnly: true },
    { name: "Total expenses claimed %", content: d => formatPercentageForDisplay(d.expensePercentage), printOnly: true },
    { name: "Claimed expense A$", content: d => formatMoneyForDisplay(calculateMarketingVisitClaimedExpense(d)), printOnly: true },
    { name: "Number of eligible working days claimed", content: d => d.eligibleWorkingDays, printOnly: true },
    { name: "Overseas allowance claimed A$", content: d => formatMoneyForDisplay((d.eligibleWorkingDays || 0) * 350), printOnly: true },
    { name: "Total claimed A$", content: d => formatMoneyForDisplay(calculateMarketingVisitAmount(d))},
    { name: "Notes", content: d => d.notes, printOnly: true }
];


export const calculateMarketingVisitClaimedExpense = (d: MarketingVisitEntry): number => {
    return customRound((d.expenseAmount || 0) * (d.expensePercentage || 0));
}

export const calculateMarketingVisitAmount = (d: MarketingVisitEntry): number => {
    return calculateMarketingVisitClaimedExpense(d) + (d.eligibleWorkingDays || 0) * 350;
}

export const calculateMarketingVisitTotal = (entries: MarketingVisitEntry[]): number => {
    let total = 0;
    for (let e of entries) {
        total += calculateMarketingVisitAmount(e);
    }
    return total;
}

export const copyFromParent = (mv: MarketingVisitEntry, parentTrip: MarketingVisitEntry) => {
    mv.purpose = parentTrip.purpose;
    mv.markets = parentTrip.markets.map(x => x);
    mv.name = parentTrip.name;
    mv.position = parentTrip.position;
    mv.departureDate = parentTrip.departureDate;
    mv.returnDate = parentTrip.returnDate
    mv.eligibleWorkingDays = null;
}

export const sortMarketingVisits = (a: MarketingVisitEntry, b: MarketingVisitEntry): number => {
    if (a.invalid && !b.invalid)
        return -1;

    if (b.invalid && !a.invalid)
        return 1;

    if (a.tripNo < b.tripNo)
        return -1;
    else if (a.tripNo > b.tripNo)
        return 1;

    if (a.primary)
        return -1

    return sortSchedules(a.invalid, b.invalid, a.reference, b.reference);
}

export const sortMarketingVisitsByAmountDesc = (a: MarketingVisitEntry, b: MarketingVisitEntry): number => {
    return calculateMarketingVisitAmount(b) - calculateMarketingVisitAmount(a);
}

const VisitExpenseType = {
    Airfare: 1,
    AirTicketCostsOther: 2,
    OVAOnly: 3
};

const VisitExpenseTypeDataSource =
[
    { value: VisitExpenseType.Airfare, label: 'Airfare' },
    { value: VisitExpenseType.AirTicketCostsOther, label: 'Air ticket costs - other' },
    { value: VisitExpenseType.OVAOnly, label: 'OVA only' }
];


export interface MarketingVisitEntryDetailsProps {
    id: string
    model: MarketingVisitEntry
    markets: Option<string>[]
    onChange: { (v: MarketingVisitEntry, close: boolean): any }
    onClose: { (): any }
    onRemove: { (): any }
    importing: boolean
    preview?: boolean
    claim: ClaimModel
    tableModel: MarketingVisitEntry[]
}

export interface MarketingVisitEntryDetailsState {
    model: MarketingVisitEntry
    validated: boolean
}

export class MarketingVisitEntryDetails extends React.Component<MarketingVisitEntryDetailsProps, MarketingVisitEntryDetailsState> implements ISection {
    tripNo: NumberField
    reference: TextField
    purpose: TextField
    markets: TextMultiSelectField
    name: TextField
    position: TextField
    departureDate: DateField
    returnDate: DateField
    expenseType: NumericSelectField
    eligibleWorkingDays: NumberField
    evidence: TextField
    paymentDate: DateField
    paymentMethod: NumericSelectField
    expenseAmount: MoneyField
    expensePercentage: PercentageField
    notes: MultiLineField

    constructor(props: MarketingVisitEntryDetailsProps) {
        super(props);

        let model: MarketingVisitEntry = this.props.model ? { ...this.props.model } : {
            id: newGuid(),
            tripNo: null,
            reference: '',
            purpose: '',
            markets: [],
            name: '',
            position: '',
            departureDate: null,
            returnDate: null,
            expenseType: null,
            eligibleWorkingDays: null,
            evidence: '',
            date: null,
            paymentDate: null,
            paymentMethod: null,
            expenseAmount: null,
            expensePercentage: null,
            invalid: false,
            notes: '',
            primary: true
        };

        if (model.markets == null)
            model.markets = [];        

        this.state = {
            model,
            validated: false,
        };
    }

    validate = (callback: { (res: boolean): any }) => {
        this.setState({ validated: true }, () => {
            validateFields(res => this.onChange({ invalid: !res }, () => callback(res)),
                this.tripNo,
                this.reference,
                this.purpose,
                this.markets,
                this.name,
                this.position,
                this.departureDate,
                this.returnDate,
                this.expenseType,
                this.eligibleWorkingDays,
                this.evidence,
                this.paymentDate,
                this.paymentMethod,
                this.expenseAmount,
                this.expensePercentage,
                this.notes
            );
        });
    }

    saveAndClose = (callback?: { (res: boolean): any }) => {
        this.validate(res => {
            if (res) {
                this.props.onChange(this.state.model, true);
            }
            callback && callback(res);
        });
    }

    remove = () => {
        if (this.state.model.primary) {
            if (confirm(`The trip that you are removing contains expense lines.\nIf you remove the trip all expense lines will also be removed.\nDo you wish to continue?`)) {
                this.props.onRemove();
            }
        }
        else {
            this.props.onRemove();
        }
    }

    componentDidMount() {
        if (this.props.importing) {
            let mv = { ...this.state.model };
            let parentTrip = this.getParentTrip(mv);

            if (parentTrip != null) {
                copyFromParent(mv, parentTrip);

                mv.primary = false;
            }
            else {
                mv.primary = true;

                if (mv.returnDate && mv.departureDate && mv.primary && mv.expenseType != VisitExpenseType.AirTicketCostsOther) {
                    let tripDays = this.calculateTripDays();
                    if (mv.eligibleWorkingDays == null || mv.eligibleWorkingDays > tripDays)
                        mv.eligibleWorkingDays = tripDays;
                }
            }

            //Clear trip expenses if expense type is OVA only
            if (mv.expenseType == VisitExpenseType.OVAOnly)
            {
                mv.expenseAmount = null;
                mv.expensePercentage = null;
            }
            else if (mv.expenseType == VisitExpenseType.AirTicketCostsOther) {
                mv.eligibleWorkingDays = null;
            }

            this.setState({model: mv}, () => {
                this.validate(res => {
                    var temp = { invalid: !res }
                    this.props.onChange({ ...this.state.model, ...temp }, true);
                })
            })
        }
        else {
            if (this.state.model.invalid) {
                this.validate(() => { });
            }
        }
    }

    componentDidUpdate(prevProps: MarketingVisitEntryDetailsProps, prevState: MarketingVisitEntryDetailsState) {
        let mv = { ...this.state.model };
        let modelUpdated = false;

        if (mv.tripNo != prevState.model.tripNo) {
            let parentTrip = this.getParentTrip(mv);

            if (parentTrip != null) {
                copyFromParent(mv, parentTrip);

                mv.primary = false;
                modelUpdated = true;
            }
            else {
                mv.primary = true;
                modelUpdated = true;
            }
        }

        if ((mv.departureDate != prevState.model.departureDate
            || mv.returnDate != prevState.model.returnDate
            || mv.markets != prevState.model.markets
            || (mv.expenseType != prevState.model.expenseType && !mv.eligibleWorkingDays))   //Only recalculate in this case if eligible days is blank
            && mv.expenseType != VisitExpenseType.AirTicketCostsOther) {   //in case market changed to Australia 

            if (mv.returnDate && mv.departureDate && mv.primary) {
                mv.eligibleWorkingDays = this.calculateTripDays();
                modelUpdated = true;
            }
        }        

        if (this.state.model.expenseType != prevState.model.expenseType) {
            if (this.state.model.expenseType == VisitExpenseType.OVAOnly) {
                mv.expenseAmount = null;
                mv.expensePercentage = null;

                modelUpdated = true;
            }
            else if (this.state.model.expenseType == VisitExpenseType.AirTicketCostsOther) {
                mv.eligibleWorkingDays = null;

                modelUpdated = true;
            }
        }


        if (modelUpdated)
            this.setState({ model: mv })
    }

    onChange = (change: Partial<MarketingVisitEntry>, callback?: {():any}) => {
        this.setState({
            model: { ...this.state.model, ...change }
        }, callback);
    }

    getParentTrip(thisEntry: MarketingVisitEntry): MarketingVisitEntry {
        let matches = this.props.tableModel.filter(r => r.primary && r.tripNo == thisEntry.tripNo && thisEntry.id != r.id);

        return matches.length > 0 ? matches[0] : null;
    }

    calculateTripDays(): number {
        if (!this.state.model.departureDate || !this.state.model.returnDate)
            return null;

        let australiaMatch = this.props.markets.filter(r => r.label == "Australia");
        if (australiaMatch.length > 0 && this.state.model.markets.filter(m => m == australiaMatch[0].value).length > 0)
            return 0;

        let grantStart = grantPeriodStart(this.props.claim.claimYear, this.props.claim.claimTwoYears);
        let grantEnd = grantPeriodEnd(this.props.claim.claimYear);

        let departureDate = getDateFromString(this.state.model.departureDate);
        let returnDate = getDateFromString(this.state.model.returnDate);

        let startDate = departureDate < grantStart ? grantStart : departureDate;
        let endDate = returnDate > grantEnd ? grantEnd : returnDate;

        let eligibleWorkingDays = calculateDaysInclusive(startDate, endDate);

        if (eligibleWorkingDays > 21)
            eligibleWorkingDays = 21;
        

        
        return eligibleWorkingDays;
    }

    validateExpense(): string {
        if (this.state.validated
            && this.state.model.expenseType != VisitExpenseType.OVAOnly
            && this.state.model.primary && !this.state.model.eligibleWorkingDays && !this.state.model.expenseAmount)
            return "'Claimed expense' or 'Overseas Allowance Claimed' is required.";

        return "";
    }

    validateEligibleDays(): string {
        //If expense type is OVA Only and allowance is calculated to be 0
        if (this.state.validated && this.state.model.expenseType == VisitExpenseType.OVAOnly && (!this.state.model.eligibleWorkingDays))
            return "Number of eligible working days was calculated as 0 based on the trip dates and hence OVA cannot be claimed for this trip.";

        return this.validateExpense();
    }

    validateDates(): string {
        if (!this.state.model.primary || !this.state.model.departureDate || !this.state.model.returnDate || !this.state.model.name || !this.state.model.position)
            return "";

        let currentDepartureDate = getDateFromString(this.state.model.departureDate);
        let currentReturnDate = getDateFromString(this.state.model.returnDate);

        if (currentReturnDate < currentDepartureDate)
            return "Return date cannot be before the departure date.";

        let overlaps = this.props.tableModel.filter(r => {
            let otherDepartureDate = getDateFromString(r.departureDate);
            let otherReturnDate = getDateFromString(r.returnDate);

            return this.state.model.id != r.id
                && this.state.model.tripNo != r.tripNo
                && (this.state.model.name || "").toLowerCase() == (r.name || "").toLowerCase()
                && (this.state.model.position || "").toLowerCase() == (r.position || "").toLowerCase()
                && ((currentDepartureDate > otherDepartureDate && currentDepartureDate < otherReturnDate)
                || (currentReturnDate > otherDepartureDate && currentReturnDate < otherReturnDate)
                || (currentDepartureDate <= otherDepartureDate && currentReturnDate >= otherReturnDate))
        });

        if (overlaps.length > 0)
            return `There is an overlap in the travel dates with trip: '${overlaps[0].tripNo}'. Please review the details of the trips.`;

        return "";
    }

    render() {
        let model = this.state.model;

        return <>
            <NumberField ref={r => this.tripNo = r}
                id={`${this.props.id}-tripNo`}
                label="Trip No."
                value={model.tripNo}
                onChange={tripNo => this.onChange({ tripNo })}
                block={false}
                required={true}
                maxLength={10}
                disabled={this.state.model.primary && this.state.model.tripNo != undefined && this.state.model.tripNo != null}
                preview={this.props.preview}
                className="mt-0"
            />
            <TextField ref={r => this.reference = r}
                id={`${this.props.id}-reference`}
                label="Your reference"
                value={model.reference}
                onChange={reference => this.onChange({ reference })}
                block={false}
                maxLength={10}
                preview={this.props.preview}
            />
            <TextField ref={r => this.purpose = r}
                id={`${this.props.id}-purpose`}
                label="Purpose of trip"
                value={model.purpose}
                onChange={purpose => this.onChange({ purpose })}
                required={true}
                maxLength={80}
                disabled={!this.state.model.primary}
                preview={this.props.preview}
            />
            <TextMultiSelectField ref={r => this.markets = r}
                id={`${this.props.id}-markets`}
                label="Destination(s)"
                value={model.markets}
                onChange={markets => this.onChange({ markets })}
                dataSource={this.props.markets}
                required={true}
                disabled={!this.state.model.primary}
                preview={this.props.preview}
            />
            <TextField ref={r => this.name = r}
                id={`${this.props.id}-name`}
                label="Name of traveller"
                value={model.name}
                onChange={name => this.onChange({ name })}
                maxLength={50}
                required={true}
                disabled={!this.state.model.primary}
                preview={this.props.preview}
            />
            <TextField ref={r => this.position = r}
                id={`${this.props.id}-position`}
                label="Position of traveller"
                value={model.position}
                onChange={position => this.onChange({ position })}
                maxLength={50}
                required={true}
                disabled={!this.state.model.primary}
                preview={this.props.preview}
            />
            <DateField ref={r => this.departureDate = r}
                id={`${this.props.id}-departureDate`}
                label="Travel departure date"
                value={model.departureDate}
                onChange={departureDate => this.onChange({ departureDate })}
                min={grantPeriodStart(this.props.claim.claimYear, this.props.claim.claimTwoYears, -1)}
                max={grantPeriodEnd(this.props.claim.claimYear, 1)}
                required={true}
                preview={this.props.preview}
                disabled={!this.state.model.primary}
                errorMessage={this.validateDates()}
            />
            <DateField ref={r => this.returnDate = r}
                id={`${this.props.id}-returnDate`}
                label="Travel return date"
                value={model.returnDate}
                onChange={returnDate => this.onChange({ returnDate })}
                min={grantPeriodStart(this.props.claim.claimYear, this.props.claim.claimTwoYears, -1)}
                max={grantPeriodEnd(this.props.claim.claimYear, 1)}
                required={true}
                preview={this.props.preview}
                disabled={!this.state.model.primary}
                errorMessage={this.validateDates()}
            />
            <NumericSelectField ref={r => this.expenseType = r}
                id={`${this.props.id}-expenseType`}
                label="Describe the expense"
                value={model.expenseType}
                onChange={expenseType => this.onChange({ expenseType })}
                dataSource={this.state.model.primary ? VisitExpenseTypeDataSource : VisitExpenseTypeDataSource.filter(r => r.value != VisitExpenseType.OVAOnly)}
                required={true}
                preview={this.props.preview}
            />            
            <TextField ref={r => this.evidence = r}
                id={`${this.props.id}-evidence`}
                label="Supporting evidence"
                value={model.evidence}
                onChange={evidence => this.onChange({ evidence })}
                required={true}
                maxLength={80}
                preview={this.props.preview}
            />
            <DateField ref={r => this.paymentDate = r}
                id={`${this.props.id}-paymentDate`}
                label="Payment date"
                value={model.paymentDate}
                onChange={paymentDate => this.onChange({ paymentDate })}
                min={grantPeriodStart(this.props.claim.claimYear, this.props.claim.claimTwoYears)}
                max={grantPeriodEnd(this.props.claim.claimYear)}
                required={model.expenseAmount ? true : false}
                preview={this.props.preview}
            />
            <NumericSelectField ref={r => this.paymentMethod = r}
                id={`${this.props.id}-paymentMethod`}
                label="Payment method"
                value={model.paymentMethod}
                onChange={paymentMethod => this.onChange({ paymentMethod })}
                dataSource={paymentMethodDataSource}
                block={false}
                required={model.expenseAmount ? true : false}
                preview={this.props.preview}
            />
            <div className="clearfix">
                <MoneyField ref={r => this.expenseAmount = r}
                    className="float-left mr-3"
                    id={`${this.props.id}-expenseAmount`}
                    label="Total expenses $A"
                    value={model.expenseAmount}
                    onChange={expenseAmount => this.onChange({ expenseAmount })}
                    preview={this.props.preview}
                    required={!this.state.model.primary}
                    disabled={model.expenseType == VisitExpenseType.OVAOnly}
                    errorMessage={this.validateExpense()}
                />
                <PercentageField ref={r => this.expensePercentage = r}
                    className="float-left mr-3"
                    id={`${this.props.id}-expensePercentage`}
                    label="Total expenses claimed %"
                    value={model.expensePercentage}
                    onChange={expensePercentage => this.onChange({ expensePercentage })}
                    required={model.expenseAmount ? true : false}
                    disabled={model.expenseType == VisitExpenseType.OVAOnly}
                    preview={this.props.preview}
                />                
                <MoneyField
                    className="float-left"
                    id={`${this.props.id}-expenseClaimed`}
                    label="Claimed expense"
                    value={customRound((model.expenseAmount || 0) * (model.expensePercentage || 0))}
                    onChange={expenseClaimed => { }}
                    required={true}
                    preview={this.props.preview}
                    disabled={true}
                />
            </div>
            <div className="clearfix">
                <NumberField ref={r => this.eligibleWorkingDays = r}
                    className="float-left mr-3"
                    id={`${this.props.id}-eligibleWorkingDays`}
                    label="Number of eligible working days claimed"
                    value={model.eligibleWorkingDays}
                    onChange={eligibleWorkingDays => this.onChange({ eligibleWorkingDays })}
                    required={false}
                    max={this.calculateTripDays()}
                    wholeNumbersOnly={true}
                    preview={this.props.preview}
                    disabled={!this.state.model.primary || model.expenseType == VisitExpenseType.AirTicketCostsOther}
                    errorMessage={this.validateEligibleDays()}
                />
                <MoneyField
                    className="float-left"
                    id={`${this.props.id}-osAllowanceClaimed`}
                    label="Overseas Allowance Claimed"
                    value={(model.eligibleWorkingDays || 0) * 350}
                    onChange={osAllowanceClaimed => { }}
                    required={true}
                    preview={this.props.preview}
                    disabled={true}
                    />
            </div>
            <MultiLineField ref={r => this.notes = r}
                id={`${this.props.id}-notes`}
                label="Notes"
                value={model.notes}
                onChange={notes => this.onChange({ notes })}
                required={false}
                maxLength={4000}
                preview={this.props.preview}
            />
            <ShowIf condition={!this.props.preview} className="clearfix mt-3">
                <Button id={`${this.props.id}-btnSave`} className="emdg-btn float-right" onClick={e => this.saveAndClose()}>Complete row</Button>
                <Button id={`${this.props.id}-btnCancel`} className="emdg-btn emdg-btn--secondary float-left mr-2" onClick={this.props.onClose} >Cancel</Button>
                <Button id={`${this.props.id}-btnRemove`}  className="emdg-btn emdg-btn--secondary float-left" onClick={e => this.remove()} style={{ display: this.props.model ? "" : "none" }}>Remove</Button>
            </ShowIf>
        </>
    }    
}